import { useCallback, useEffect, useRef, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import { DndProvider, useDrag, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import api from "../../lib/api"
import { getErrorMessage, getPublishErrorMessage } from "../../lib/errors"
import { toastError, toastSuccess } from "../../lib/toast"
import {
  StyledSelect,
  Toggle,
  inputCls,
  textareaCls,
  labelCls,
  btnSave,
  btnSecondary,
} from "../../components/admin/FormComponents"
import { SearchMultiSelect } from "../../components/admin/SearchMultiSelect"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import type { VideoStatus } from "../../types/video"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import TagSearchInput from "../../components/admin/TagSearchInput"
import { ChipInput } from "../../components/ui/ChipInput"
import { COURSE_CATEGORIES } from "../../config/courseCategories"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { CourseShell, type CourseTabKey } from "../../components/academy/CourseShell"
import { DragHandle } from "../../components/cccd/DraggableRow"
import { TrashBinIcon } from "../../icons"
import { LoadState } from "@/components/ui/LoadState"

const SECTION_DND_TYPE = "course-section"
const LESSON_DND_TYPE = "course-lesson"

// ── Icons (stroke-based, 16/20px grid, currentColor) ──────────

function IconChevron({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconArrowUp({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 11V3M7 3 3.5 6.5M7 3l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconArrowDown({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 3v8M7 11 3.5 7.5M7 11l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconPlay({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M5 3.5v9l8-4.5-8-4.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

function IconFilm({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="3" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.5 3v10M10.5 3v10M2.5 6.5h3M10.5 6.5h3M2.5 9.5h3M10.5 9.5h3" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}

function IconBookOpen({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 4.5c-1-1-2.6-1.5-4.5-1.5v8.5c1.9 0 3.5.5 4.5 1.5m0-8.5c1-1 2.6-1.5 4.5-1.5v8.5c-1.9 0-3.5.5-4.5 1.5m0-8.5v8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconClipboard({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3.5" y="3" width="9" height="11" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M6 3V2.3h4V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5.5 7.5h5M5.5 10h5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

function IconFileText({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 2.5h5.5L12 5v8.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M5.5 8h5M5.5 10.3h5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

function LessonTypeIcon({ lesson, className = "" }: { lesson: Lesson; className?: string }) {
  if (lesson.isPrivateVideo) return <IconFilm className={className} />
  if (lesson.youtubeVideoId) return <IconPlay className={className} />
  if (lesson.lessonType === "reading") return <IconBookOpen className={className} />
  if (lesson.lessonType === "assignment") return <IconClipboard className={className} />
  if (lesson.lessonType === "document") return <IconFileText className={className} />
  return <IconFilm className={className} />
}

// ── Types ──────────────────────────────────────────────────────

type Difficulty = "beginner" | "intermediate" | "advanced"
type LessonType = "video" | "reading" | "assignment" | "document"
type Course = {
  id: string
  title: string
  category?: string | null
  tags?: string | null
  description?: string | null
  shortDescription?: string | null
  thumbnailUrl?: string | null
  difficultyLevel: Difficulty
  isFree: boolean
  isCccdCourse: boolean
  isPublished: boolean
  estimatedHours?: number | null
  lesson_count?: number
  student_count?: number
}

type Lesson = {
  id: string
  courseId?: string | null // nullable — legacy field
  title: string
  description?: string | null
  youtubeUrl?: string | null
  youtubeVideoId?: string | null
  orderIndex: number // from junction row (per-course)
  durationMinutes?: number | null
  isPreview: boolean
  isPrivateVideo: boolean
  project_count?: number
  videoId?: string | null
  videoStatus?: VideoStatus | null
  videoDuration?: number | null
  videoThumbnailPath?: string | null
  sectionId?: string | null // from junction row (per-course)
  lessonType?: LessonType
  documentPath?: string | null
}

type CourseSection = {
  id: string
  courseId: string
  title: string
  description?: string | null
  orderIndex: number
  lessons: Lesson[]
}

type CourseInstructor = {
  id: string
  userId: string
  role: "lead" | "contributor"
  orderIndex: number
  user: { id: string; name: string; email: string; avatarUrl?: string | null; bio?: string | null }
}

type InstructorUser = { id: string; name: string; email: string; role: string; avatarUrl?: string | null }

type PrerequisiteCourse = { id: string; slug: string; title: string }

type SectionForm = { title: string }

type InfoForm = {
  title: string
  short_description: string
  thumbnail_url: string
  description: string
  category: string
  tags: string[]
  difficulty_level: Difficulty
  price: number
  estimated_hours: number
  is_free: boolean
  is_cccd_course: boolean
  is_published: boolean
}

const initialSectionForm: SectionForm = { title: "" }

const initialInfoForm: InfoForm = {
  title: "",
  short_description: "",
  thumbnail_url: "",
  description: "",
  category: "",
  tags: [],
  difficulty_level: "beginner",
  price: 0,
  estimated_hours: 0,
  is_free: true,
  is_cccd_course: false,
  is_published: false,
}

function tagsFromString(tags: string | null | undefined): string[] {
  return (tags ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
}

function difficultyTone(level: Difficulty): string {
  if (level === "advanced") return "bg-[color:rgba(234,179,8,0.18)] text-[var(--alas-gold)]"
  if (level === "intermediate") return "bg-[color:rgba(59,130,246,0.14)] text-[var(--accent-blue)]"
  return "bg-[color:rgba(34,197,94,0.14)] text-success-700 dark:text-success-300"
}

// ── Drag and drop (secciones + lecciones) ─────────────────────

type LessonDragItem = { id: string; index: number; containerId: string | null }
type SectionDragItem = { id: string; index: number }

// ── LessonRow component ──────────────────────────────────────

type LessonRowProps = {
  lesson: Lesson
  index: number
  containerId: string | null
  /** false para lecciones sin sección: solo se pueden arrastrar hacia una sección, nunca recibir un drop. */
  allowDrop: boolean
  isFirst: boolean
  isLast: boolean
  reordering: boolean
  onEdit: () => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onTogglePreview: () => void
  onHoverMove: (dragId: string, targetContainerId: string | null, hoverIndex: number) => void
  onDragStart: () => void
  onDragEnd: () => void
}

function LessonRow({
  lesson, index, containerId, allowDrop, isFirst, isLast, reordering,
  onEdit, onDelete, onMoveUp, onMoveDown, onTogglePreview, onHoverMove, onDragStart, onDragEnd,
}: LessonRowProps) {
  const ref = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLSpanElement>(null)

  const [, drop] = useDrop<LessonDragItem>({
    accept: LESSON_DND_TYPE,
    hover(item) {
      if (item.id === lesson.id) return
      onHoverMove(item.id, containerId, index)
      item.index = index
      item.containerId = containerId
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: LESSON_DND_TYPE,
    item: (): LessonDragItem => {
      onDragStart()
      return { id: lesson.id, index, containerId }
    },
    end: onDragEnd,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  if (allowDrop) drop(ref)
  drag(handleRef)

  return (
    <div ref={ref} className={`group flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${isDragging ? "opacity-40" : ""}`}>
      <span ref={handleRef} className="shrink-0 cursor-grab text-gray-300 dark:text-gray-600" title="Arrastrar para reordenar">
        <DragHandle />
      </span>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        <LessonTypeIcon lesson={lesson} />
      </span>
      <span className="w-6 text-center text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">{lesson.orderIndex}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{lesson.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {lesson.durationMinutes ? <span className="text-xs text-gray-500">{lesson.durationMinutes} min</span> : null}
          <button
            type="button"
            onClick={onTogglePreview}
            title={lesson.isPreview ? "Quitar preview — clic para hacerla privada" : "Marcar como preview — accesible sin inscripción"}
            className={`text-[11px] rounded-full px-1.5 py-0.5 font-medium transition-colors ${
              lesson.isPreview
                ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                : "bg-gray-100 text-gray-400 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
            }`}
          >
            Preview
          </button>
          {lesson.videoStatus && (
            <span className="text-[11px] rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 font-medium capitalize">
              {lesson.videoStatus}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
        <button type="button" onClick={onMoveUp} disabled={isFirst || reordering} aria-label="Mover arriba" title="Mover arriba"
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200">
          <IconArrowUp />
        </button>
        <button type="button" onClick={onMoveDown} disabled={isLast || reordering} aria-label="Mover abajo" title="Mover abajo"
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200">
          <IconArrowDown />
        </button>
        <span className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
        <button type="button" onClick={onEdit}
          className="rounded px-2 py-1 text-xs font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10">Editar</button>
        <button type="button" onClick={onDelete}
          className="rounded px-2 py-1 text-xs font-medium text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-500/10">Eliminar</button>
      </div>
    </div>
  )
}

// Zona de drop dentro de una sección: cubre tanto el estado vacío (sin
// lecciones) como el hueco al final de la lista, para poder soltar una
// lección ahí aunque no haya ninguna fila encima para hacer hover.
type LessonDropZoneProps = {
  containerId: string | null
  targetIndex: number
  variant: "empty" | "end"
  onHoverMove: (dragId: string, targetContainerId: string | null, hoverIndex: number) => void
  onAddLesson?: () => void
}

function LessonDropZone({ containerId, targetIndex, variant, onHoverMove, onAddLesson }: LessonDropZoneProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [{ isOver }, drop] = useDrop<LessonDragItem, void, { isOver: boolean }>({
    accept: LESSON_DND_TYPE,
    hover(item) {
      onHoverMove(item.id, containerId, targetIndex)
      item.index = targetIndex
      item.containerId = containerId
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  })
  drop(ref)

  if (variant === "empty") {
    return (
      <div ref={ref} className={`px-4 py-6 text-center transition-colors ${isOver ? "bg-brand-50 dark:bg-brand-500/10" : ""}`}>
        <p className="text-xs text-gray-500 dark:text-gray-400">Sin lecciones. Arrastra una lección aquí, o</p>
        {onAddLesson && (
          <button type="button" onClick={onAddLesson} className="text-xs text-brand-500 hover:underline mt-1">
            + Agregar lección a esta sección
          </button>
        )}
      </div>
    )
  }

  return <div ref={ref} className={`h-2 transition-colors ${isOver ? "bg-brand-100 dark:bg-brand-500/20" : ""}`} />
}

// ── SectionQuizPanel — ai-generated-section-quizzes ─────────────
// Self-contained: fetches/mutates its own state so it doesn't need to be
// threaded through CourseManagerPage's section/lesson state machine.

type SectionQuizLessonStatus = {
  lessonId: string
  title: string
  available: boolean
  source?: "reading" | "youtube_captions"
  reason?: string
}

type SectionQuizStatus = {
  eligible: boolean
  lessons: SectionQuizLessonStatus[]
  quiz: {
    id: string
    title: string
    passingScore: number
    questionCount: number
    status: "draft" | "generating" | "active" | "error"
    generationError?: string | null
    attemptMode?: "individual" | "team"
    isRequired: boolean
  } | null
}

const QUIZ_STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  generating: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  error: "bg-error-100 text-error-700 dark:bg-error-500/15 dark:text-error-300",
  draft: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
}

const QUIZ_STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  generating: "Generando...",
  error: "Error",
  draft: "Borrador",
}

// El generador (Workers AI, hasta 3 intentos con timeout de 30s cada uno)
// puede tardar hasta ~90s — activar/reintentar dispara la generación en un
// Workflow y este panel hace polling a GET /status hasta que se resuelve, en
// vez de dejar el fetch de POST /section-quizzes colgado ese tiempo.
const POLL_INTERVAL_MS = 2500

const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  empty_content: "Sin contenido de lectura",
  no_captions: "Video de YouTube sin captions",
  captions_fetch_failed: "No se pudieron obtener los captions",
  unsupported_lesson_type: "Video privado (sin transcripción, fase 2)",
}

function SectionQuizPanel({ sectionId }: { sectionId: string }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<SectionQuizStatus | null>(null)
  const [activating, setActivating] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/academy/section-quizzes/${sectionId}/status`)
      setStatus(res.data.data)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [sectionId])

  useEffect(() => {
    if (open && !status) void load()
  }, [open, status, load])

  // Mientras el Workflow de generación corre en el servidor, este panel
  // simplemente vuelve a pedir el estado cada POLL_INTERVAL_MS — el efecto
  // se reprograma solo mientras status.quiz.status siga en "generating" y
  // se detiene automáticamente en cuanto pasa a "active" o "error".
  useEffect(() => {
    if (status?.quiz?.status !== "generating") return
    const timer = setTimeout(() => { void load() }, POLL_INTERVAL_MS)
    return () => clearTimeout(timer)
  }, [status?.quiz?.status, load])

  const handleActivate = useCallback(async () => {
    setActivating(true)
    try {
      await api.post("/academy/section-quizzes", { section_id: sectionId })
      await load()
    } catch (err) {
      toastError(err)
    } finally {
      setActivating(false)
    }
  }, [sectionId, load])

  const [togglingRequired, setTogglingRequired] = useState(false)

  const handleToggleRequired = useCallback(async () => {
    if (!status?.quiz) return
    setTogglingRequired(true)
    try {
      await api.patch(`/academy/section-quizzes/${status.quiz.id}`, { is_required: !status.quiz.isRequired })
      await load()
    } catch (err) {
      toastError(err)
    } finally {
      setTogglingRequired(false)
    }
  }, [status?.quiz, load])

  const handleRegenerate = useCallback(async () => {
    if (!status?.quiz) return
    setRegenerating(true)
    try {
      await api.post(`/academy/section-quizzes/${status.quiz.id}/regenerate-backup`)
      toastSuccess("Banco de respaldo regenerado")
    } catch (err) {
      toastError(err)
    } finally {
      setRegenerating(false)
    }
  }, [status?.quiz])

  return (
    <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 dark:border-gray-800 dark:bg-gray-900/30">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-brand-500 dark:text-gray-400">
        <IconChevron className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
        Quiz IA de esta sección
        {status?.quiz && (
          <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${QUIZ_STATUS_BADGE[status.quiz.status]}`}>
            {QUIZ_STATUS_LABEL[status.quiz.status]}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {loading && <LoadState compact />}

          {!loading && status && (
            <>
              <ul className="space-y-1">
                {status.lessons.map((l) => (
                  <li key={l.lessonId} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 dark:text-gray-300">{l.title}</span>
                    <span className={l.available ? "text-green-600 dark:text-green-400" : "text-gray-400"}>
                      {l.available ? (l.source === "youtube_captions" ? "Captions de YouTube" : "Contenido de lectura") : (UNAVAILABLE_REASON_LABELS[l.reason ?? ""] ?? "Sin texto disponible")}
                    </span>
                  </li>
                ))}
              </ul>

              {status.quiz?.status === "generating" && (
                <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <span className="inline-block size-3 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
                  Generando preguntas con IA... esto puede tardar hasta 1-2 minutos.
                </p>
              )}

              {(!status.quiz || status.quiz.status === "error") && (
                status.eligible ? (
                  <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    {status.quiz?.status === "error" && (
                      <p className="text-xs text-error-600 dark:text-error-400">
                        La generación anterior falló{status.quiz.generationError ? `: ${status.quiz.generationError}` : ""}. Puedes reintentar.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Se llamará "Examen", con nota mínima 70% y entre {Math.max(1, status.lessons.length)} y {Math.max(1, status.lessons.length) * 2} preguntas (según las {status.lessons.length} lecciones de esta sección) — la IA decide cuántas dentro de ese rango.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <button type="button" onClick={handleActivate} disabled={activating} className={btnSave}>
                        {activating ? "Iniciando..." : status.quiz?.status === "error" ? "Reintentar" : "Activar quiz IA"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Esta sección no tiene contenido de texto suficiente para generar un quiz IA (ver estados arriba).
                  </p>
                )
              )}

              {status.quiz?.status === "active" && (
                <label className="flex w-fit items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={status.quiz.isRequired}
                    disabled={togglingRequired}
                    onChange={handleToggleRequired}
                    className="size-3.5 rounded border-gray-300"
                  />
                  Obligatorio para completar el curso (si se desmarca, es práctica/opcional)
                </label>
              )}

              {status.quiz?.status === "active" && (
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={handleRegenerate} disabled={regenerating} className={btnSecondary}>
                    {regenerating ? "Regenerando..." : "Regenerar banco de respaldo"}
                  </button>
                  <button type="button" onClick={() => navigate(`/instructor/section-quizzes/${status.quiz!.id}/attempts`)} className={btnSecondary}>
                    Ver intentos
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── SectionCard component ──────────────────────────────────────

type SectionCardProps = {
  section: CourseSection
  index: number
  totalSections: number
  sectionLessons: Lesson[]
  isCollapsed: boolean
  isEditing: boolean
  sectionForm: SectionForm
  savingSection: boolean
  reorderingLessons: boolean
  reorderingSections: boolean
  onToggleCollapse: () => void
  onStartEdit: () => void
  onCancelEdit: () => void
  onSave: (e: FormEvent<HTMLFormElement>) => void
  onDelete: () => void
  onSetTitle: (title: string) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onMoveSectionHover: (dragIndex: number, hoverIndex: number) => void
  onSectionDragEnd: () => void
  onStartCreateLesson: () => void
  onEditLesson: (lesson: Lesson) => void
  onDeleteLesson: (lessonId: string) => void
  onMoveLessonUp: (lessonId: string) => void
  onMoveLessonDown: (lessonId: string) => void
  onToggleLessonPreview: (lessonId: string) => void
  onLessonHoverMove: (dragId: string, targetContainerId: string | null, hoverIndex: number) => void
  onLessonDragStart: () => void
  onLessonDragEnd: () => void
}

function SectionCard({
  section, index, totalSections, sectionLessons, isCollapsed, isEditing, sectionForm, savingSection,
  reorderingLessons, reorderingSections,
  onToggleCollapse, onStartEdit, onCancelEdit, onSave, onDelete, onSetTitle, onMoveUp, onMoveDown,
  onMoveSectionHover, onSectionDragEnd,
  onStartCreateLesson, onEditLesson, onDeleteLesson, onMoveLessonUp, onMoveLessonDown, onToggleLessonPreview,
  onLessonHoverMove, onLessonDragStart, onLessonDragEnd,
}: SectionCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLSpanElement>(null)

  const [, drop] = useDrop<SectionDragItem>({
    accept: SECTION_DND_TYPE,
    hover(item) {
      if (item.index === index) return
      onMoveSectionHover(item.index, index)
      item.index = index
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: SECTION_DND_TYPE,
    item: (): SectionDragItem => ({ id: section.id, index }),
    end: onSectionDragEnd,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  drop(ref)
  drag(handleRef)

  const sectionMinutes = sectionLessons.reduce((sum, l) => sum + (l.durationMinutes ?? 0), 0)

  return (
    <div ref={ref} className={`rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${isDragging ? "opacity-40" : ""}`}>
      {/* Section header */}
      {isEditing ? (
        <form onSubmit={onSave} className="flex items-end gap-2 bg-gray-50 p-4 dark:bg-gray-800">
          <div className="flex-1">
            <label className={labelCls}>Nombre de la sección</label>
            <input required autoFocus placeholder="Ej: Introducción al curso"
              value={sectionForm.title}
              onChange={(e) => onSetTitle(e.target.value)}
              className={inputCls} />
          </div>
          <button type="button" onClick={onCancelEdit} className={btnSecondary}>Cancelar</button>
          <button type="submit" disabled={savingSection} className={btnSave}>
            {savingSection ? "Guardando..." : "Guardar"}
          </button>
        </form>
      ) : (
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 px-4 py-3">
          <span ref={handleRef} className="shrink-0 cursor-grab text-gray-300 dark:text-gray-600" title="Arrastrar para reordenar">
            <DragHandle />
          </span>
          <button type="button" onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expandir sección" : "Colapsar sección"}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200">
            <IconChevron className={`transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`} />
          </button>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
            {index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{section.title}</p>
            <p className="text-xs text-gray-500">
              {sectionLessons.length} lección{sectionLessons.length !== 1 ? "es" : ""}
              {sectionMinutes > 0 ? ` · ${sectionMinutes} min` : ""}
            </p>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <button type="button" onClick={onMoveUp} disabled={index === 0 || reorderingSections} aria-label="Mover sección arriba" title="Mover arriba"
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700 dark:hover:text-gray-200">
              <IconArrowUp />
            </button>
            <button type="button" onClick={onMoveDown} disabled={index === totalSections - 1 || reorderingSections} aria-label="Mover sección abajo" title="Mover abajo"
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700 dark:hover:text-gray-200">
              <IconArrowDown />
            </button>
            <span className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
            <button type="button" onClick={onStartEdit}
              className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-brand-500 dark:hover:bg-gray-700">
              Editar
            </button>
            <button type="button" onClick={onDelete}
              className="rounded px-2 py-1 text-xs font-medium text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-500/10">
              Eliminar
            </button>
            <button type="button" onClick={onStartCreateLesson}
              className="rounded px-2 py-1 text-xs font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10">
              + Lección
            </button>
          </div>
        </div>
      )}
      {/* Section lessons */}
      {!isCollapsed && (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {sectionLessons.length === 0 ? (
            <LessonDropZone
              containerId={section.id}
              targetIndex={0}
              variant="empty"
              onHoverMove={onLessonHoverMove}
              onAddLesson={onStartCreateLesson}
            />
          ) : (
            <>
              {sectionLessons.map((lesson, idx) => (
                <LessonRow
                  key={lesson.id}
                  lesson={lesson}
                  index={idx}
                  containerId={section.id}
                  allowDrop
                  isFirst={idx === 0}
                  isLast={idx === sectionLessons.length - 1}
                  reordering={reorderingLessons}
                  onEdit={() => onEditLesson(lesson)}
                  onDelete={() => onDeleteLesson(lesson.id)}
                  onMoveUp={() => onMoveLessonUp(lesson.id)}
                  onMoveDown={() => onMoveLessonDown(lesson.id)}
                  onTogglePreview={() => onToggleLessonPreview(lesson.id)}
                  onHoverMove={onLessonHoverMove}
                  onDragStart={onLessonDragStart}
                  onDragEnd={onLessonDragEnd}
                />
              ))}
              <LessonDropZone
                containerId={section.id}
                targetIndex={sectionLessons.length}
                variant="end"
                onHoverMove={onLessonHoverMove}
              />
            </>
          )}
        </div>
      )}
      {!isCollapsed && <SectionQuizPanel sectionId={section.id} />}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────

export default function CourseManagerPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [activeTab, setActiveTab] = useState<CourseTabKey>(isEdit ? "contenido" : "info")
  const [pendingConfirm, setPendingConfirm] = useState<{ title: string; message: string; confirmLabel: string; onConfirm: () => void } | null>(null)

  // Course + Info form
  const [course, setCourse] = useState<Course | null>(null)
  const [infoForm, setInfoForm] = useState<InfoForm>(initialInfoForm)
  const [loading, setLoading] = useState(isEdit)
  const [savingInfo, setSavingInfo] = useState(false)

  // Lessons
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [reorderingLessons, setReorderingLessons] = useState(false)
  const lessonDragSnapshotRef = useRef<Lesson[] | null>(null)

  // Sections
  const [sections, setSections] = useState<CourseSection[]>([])
  const [loadingSections, setLoadingSections] = useState(false)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null)
  const [isCreatingSection, setIsCreatingSection] = useState(false)
  const [savingSection, setSavingSection] = useState(false)
  const [sectionForm, setSectionForm] = useState<SectionForm>(initialSectionForm)
  const [reorderingSections, setReorderingSections] = useState(false)

  // Instructors
  const [instructors, setInstructors] = useState<CourseInstructor[]>([])
  const [allUsers, setAllUsers] = useState<InstructorUser[]>([])
  const [instructorsPage, setInstructorsPage] = useState(1)
  const [pendingInstructor, setPendingInstructor] = useState<InstructorUser | null>(null)
  const [pendingInstructorRole, setPendingInstructorRole] = useState<"lead" | "contributor">("lead")
  const [savingInstructor, setSavingInstructor] = useState(false)
  const [removingInstructorId, setRemovingInstructorId] = useState<string | null>(null)

  // Prerequisites
  const [prerequisites, setPrerequisites] = useState<PrerequisiteCourse[]>([])
  const [allCourses, setAllCourses] = useState<{ id: string; title: string }[]>([])
  const [prerequisitesPage, setPrerequisitesPage] = useState(1)
  const [pendingPrerequisite, setPendingPrerequisite] = useState<{ id: string; title: string } | null>(null)
  const [savingPrerequisite, setSavingPrerequisite] = useState(false)
  const [removingPrerequisiteId, setRemovingPrerequisiteId] = useState<string | null>(null)

  // Programs panel
  const [allPrograms, setAllPrograms] = useState<{ id: string; name: string; slug: string; isPublished: boolean; course_count?: number }[]>([])
  const [loadingPrograms, setLoadingPrograms] = useState(false)
  const [assignedProgramIds, setAssignedProgramIds] = useState<string[]>([])
  const [programsPage, setProgramsPage] = useState(1)
  const [pendingProgram, setPendingProgram] = useState<{ id: string; name: string } | null>(null)
  const [savingProgram, setSavingProgram] = useState(false)
  const [removingProgramId, setRemovingProgramId] = useState<string | null>(null)
  const programsLoadedRef = useRef(false) // prevents infinite retry when empty or on error

  const INSTRUCTORS_PAGE_SIZE = 10
  const PREREQUISITES_PAGE_SIZE = 10
  const PROGRAMS_PAGE_SIZE = 10

  // ── Data loading ─────────────────────────────────────────────

  const loadCourse = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const res = await api.get("/academy/courses/admin/all?limit=100&page=1")
      const all: Course[] = res.data.data?.courses ?? []
      const found = all.find((c) => c.id === id) ?? null
      setCourse(found)
      setAllCourses(all.filter((c) => c.id !== id).map((c) => ({ id: c.id, title: c.title })))
      if (found) {
        setInfoForm({
          title: found.title,
          short_description: found.shortDescription ?? "",
          thumbnail_url: found.thumbnailUrl ?? "",
          description: found.description ?? "",
          category: found.category ?? "",
          tags: tagsFromString(found.tags),
          difficulty_level: found.difficultyLevel ?? "beginner",
          price: 0,
          estimated_hours: Number(found.estimatedHours ?? 0),
          is_free: found.isFree,
          is_cccd_course: found.isCccdCourse,
          is_published: found.isPublished,
        })
      }
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [id])

  const loadLessons = useCallback(async () => {
    if (!id) return
    try {
      const res = await api.get(`/academy/lessons/course/${id}`)
      setLessons(res.data?.data ?? [])
    } catch {
      setLessons([])
    }
  }, [id])

  const loadSections = useCallback(async () => {
    if (!id) return
    setLoadingSections(true)
    try {
      const res = await api.get(`/academy/courses/admin/${id}/sections`)
      setSections(res.data.data ?? [])
    } catch {
      setSections([])
    } finally {
      setLoadingSections(false)
    }
  }, [id])

  const loadInstructors = useCallback(async () => {
    if (!id) return
    try {
      const [instRes, usersRes] = await Promise.all([
        api.get(`/academy/courses/${id}/instructors`),
        api.get("/admin/users?limit=200"),
      ])
      setInstructors(instRes.data.data ?? [])
      setAllUsers(usersRes.data.data?.users ?? [])
    } catch {
      setInstructors([])
    }
  }, [id])

  const loadPrerequisites = useCallback(async () => {
    if (!id) return
    try {
      const res = await api.get(`/academy/courses/${id}/prerequisites`)
      setPrerequisites(res.data.data ?? [])
    } catch {
      setPrerequisites([])
    }
  }, [id])

  const loadPrograms = useCallback(async () => {
    if (!id) return
    setLoadingPrograms(true)
    try {
      const allRes = await api.get("/academy/programs/admin/all?limit=200&page=1")
      const allPayload = allRes.data?.data
      const all = Array.isArray(allPayload) ? allPayload : (allPayload?.programs ?? [])
      setAllPrograms(all)
      if (all.length > 0) {
        const details = await Promise.all(all.map((p: { id: string }) => api.get(`/academy/programs/admin/${p.id}`)))
        const enrolled = details
          .map((r, i) => {
            const courses: Array<{ courseId: string }> = r.data?.data?.courses ?? []
            return courses.some((cp) => cp.courseId === id) ? all[i].id : null
          })
          .filter(Boolean) as string[]
        setAssignedProgramIds(enrolled)
      }
    } catch {
      // leave allPrograms/assignedProgramIds as-is; don't retry
    } finally {
      programsLoadedRef.current = true // always mark as attempted — prevents infinite retry
      setLoadingPrograms(false)
    }
  }, [id])

  useEffect(() => {
    void loadCourse()
    if (isEdit) {
      void loadLessons()
      void loadSections()
      void loadInstructors()
      void loadPrerequisites()
    }
  }, [isEdit, loadCourse, loadLessons, loadSections, loadInstructors, loadPrerequisites])

  // Lazy-load programs only when the tab is opened for the first time
  useEffect(() => {
    if (activeTab === "programas" && !programsLoadedRef.current && !loadingPrograms) {
      void loadPrograms()
    }
  }, [activeTab, loadingPrograms, loadPrograms])

  // ── Info handlers ─────────────────────────────────────────────

  async function onSaveInfo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSavingInfo(true)

    const payload = {
      title: infoForm.title.trim(),
      description: infoForm.description.trim() || undefined,
      short_description: infoForm.short_description.trim() || undefined,
      thumbnail_url: infoForm.thumbnail_url.trim() || undefined,
      category: infoForm.category.trim() || undefined,
      tags: infoForm.tags.length > 0 ? infoForm.tags.join(",") : undefined,
      difficulty_level: infoForm.difficulty_level,
      price: Number(infoForm.price || 0),
      estimated_hours: Number(infoForm.estimated_hours || 0),
      is_free: infoForm.is_free,
      is_cccd_course: infoForm.is_cccd_course,
      ...(isEdit ? { is_published: infoForm.is_published } : {}),
    }

    try {
      if (isEdit && id) {
        await api.put(`/academy/courses/${id}`, payload)
        qc.invalidateQueries({ queryKey: ["server-table", "/academy/courses/admin/all"] })
        toastSuccess("Curso actualizado")
        await loadCourse()
      } else {
        const res = await api.post("/academy/courses", payload)
        const newId = res.data?.data?.id
        qc.invalidateQueries({ queryKey: ["server-table", "/academy/courses/admin/all"] })
        toastSuccess("Curso creado")
        navigate(newId ? `/academy/courses/${newId}` : "/academy/courses")
        return
      }
    } catch (err) {
      toastError(payload.is_published ? getPublishErrorMessage(err) : getErrorMessage(err))
    } finally {
      setSavingInfo(false)
    }
  }

  async function onTogglePublish() {
    if (!course) return
    try {
      await api.put(`/academy/courses/${course.id}`, { is_published: !course.isPublished })
      await loadCourse()
      toastSuccess(course.isPublished ? "Curso despublicado" : "Curso publicado")
    } catch (err) {
      toastError(getPublishErrorMessage(err))
    }
  }

  // ── Lesson handlers ────────────────────────────────────────────

  function onStartCreateLesson(sectionId: string) {
    navigate(`/academy/courses/${id}/lessons/new?section=${sectionId}`)
  }

  function onStartEditLesson(lesson: Lesson) {
    navigate(`/academy/courses/${id}/lessons/${lesson.id}/edit`)
  }

  function onDeleteLesson(lessonId: string) {
    setPendingConfirm({
      title: "¿Eliminar esta lección?",
      message: "Se eliminarán también sus proyectos. Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      onConfirm: () => void confirmDeleteLesson(lessonId),
    })
  }

  async function confirmDeleteLesson(lessonId: string) {
    try {
      await api.delete(`/academy/lessons/${lessonId}`)
      await loadLessons()
      await loadCourse()
    } catch (err) {
      toastError(err)
    } finally {
      setPendingConfirm(null)
    }
  }

  async function onToggleLessonPreview(lessonId: string) {
    const lesson = lessons.find((l) => l.id === lessonId)
    if (!lesson) return
    const nextIsPreview = !lesson.isPreview
    setLessons((prev) => prev.map((l) => (l.id === lessonId ? { ...l, isPreview: nextIsPreview } : l)))
    try {
      await api.put(`/academy/lessons/${lessonId}`, { is_preview: nextIsPreview, course_id: id })
    } catch (err) {
      toastError(err)
      setLessons((prev) => prev.map((l) => (l.id === lessonId ? { ...l, isPreview: !nextIsPreview } : l)))
    }
  }

  async function persistLessonOrder(nextLessons: Lesson[]) {
    setReorderingLessons(true)
    try {
      await Promise.all(
        nextLessons.map((lesson, index) =>
          // Pass course_id so the API updates the correct junction row
          api.put(`/academy/lessons/${lesson.id}`, { order_index: index + 1, course_id: id }),
        ),
      )
      setLessons((prev) => {
        const nextOrder = new Map(nextLessons.map((l, i) => [l.id, i + 1]))
        return prev.map((l) => (nextOrder.has(l.id) ? { ...l, orderIndex: nextOrder.get(l.id)! } : l))
      })
    } catch (err) {
      toastError(err)
    } finally {
      setReorderingLessons(false)
    }
  }

  function moveLesson(lessonId: string, direction: "up" | "down") {
    const lesson = lessons.find((l) => l.id === lessonId)
    if (!lesson) return
    const containerId = lesson.sectionId ?? null
    const sorted = lessons.filter((l) => (l.sectionId ?? null) === containerId).sort((a, b) => a.orderIndex - b.orderIndex)
    const index = sorted.findIndex((l) => l.id === lessonId)
    if (index === -1) return
    const targetIndex = direction === "up" ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= sorted.length) return
    const next = [...sorted]
    ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
    void persistLessonOrder(next)
  }

  // Vía sin arrastre para mover una lección "sin sección" a una sección real —
  // el arrastre queda como atajo, pero esta es la vía garantizada.
  async function moveLessonToSection(lessonId: string, sectionId: string) {
    const targetCount = lessons.filter((l) => l.sectionId === sectionId).length
    setReorderingLessons(true)
    try {
      await api.put(`/academy/lessons/${lessonId}`, {
        section_id: sectionId,
        order_index: targetCount + 1,
        course_id: id,
      })
      setLessons((prev) =>
        prev.map((l) => (l.id === lessonId ? { ...l, sectionId, orderIndex: targetCount + 1 } : l)),
      )
    } catch (err) {
      toastError(err)
    } finally {
      setReorderingLessons(false)
    }
  }

  // Reordenamiento por arrastre: el hover reordena/mueve la lección en el
  // estado local (vista previa optimista); solo al soltar se persiste el
  // diff real contra el snapshot tomado al iniciar el arrastre.
  function moveLessonHover(dragId: string, targetContainerId: string | null, hoverIndex: number) {
    setLessons((prev) => {
      const dragLesson = prev.find((l) => l.id === dragId)
      if (!dragLesson) return prev
      const sourceContainerId = dragLesson.sectionId ?? null
      // Nunca permitir sacar una lección de su sección hacia "sin sección" —
      // solo se puede arrastrar HACIA una sección real.
      if (targetContainerId === null && sourceContainerId !== null) return prev

      const buildList = (containerId: string | null) =>
        prev.filter((l) => (l.sectionId ?? null) === containerId).sort((a, b) => a.orderIndex - b.orderIndex)

      if (sourceContainerId === targetContainerId) {
        const list = buildList(targetContainerId)
        const dragIndex = list.findIndex((l) => l.id === dragId)
        if (dragIndex === -1 || dragIndex === hoverIndex) return prev
        const next = [...list]
        const [moved] = next.splice(dragIndex, 1)
        next.splice(Math.min(hoverIndex, next.length), 0, moved)
        const reindexed = next.map((l, i) => ({ ...l, orderIndex: i + 1 }))
        const ids = new Set(reindexed.map((l) => l.id))
        return [...prev.filter((l) => !ids.has(l.id)), ...reindexed]
      }

      const sourceList = buildList(sourceContainerId).filter((l) => l.id !== dragId)
      const targetList = buildList(targetContainerId)
      const nextTarget = [...targetList]
      nextTarget.splice(Math.min(hoverIndex, nextTarget.length), 0, { ...dragLesson, sectionId: targetContainerId })
      const sourceReindexed = sourceList.map((l, i) => ({ ...l, orderIndex: i + 1 }))
      const targetReindexed = nextTarget.map((l, i) => ({ ...l, orderIndex: i + 1 }))
      const changedIds = new Set([...sourceReindexed, ...targetReindexed].map((l) => l.id))
      return [...prev.filter((l) => !changedIds.has(l.id)), ...sourceReindexed, ...targetReindexed]
    })
  }

  function onLessonDragStart() {
    if (!lessonDragSnapshotRef.current) lessonDragSnapshotRef.current = lessons
  }

  async function onLessonDragEnd() {
    const snapshot = lessonDragSnapshotRef.current
    lessonDragSnapshotRef.current = null
    if (!snapshot || !id) return
    const changed = lessons.filter((l) => {
      const before = snapshot.find((s) => s.id === l.id)
      return !before || before.orderIndex !== l.orderIndex || (before.sectionId ?? null) !== (l.sectionId ?? null)
    })
    if (changed.length === 0) return
    setReorderingLessons(true)
    try {
      await Promise.all(
        changed.map((l) =>
          api.put(`/academy/lessons/${l.id}`, { order_index: l.orderIndex, section_id: l.sectionId ?? null, course_id: id }),
        ),
      )
    } catch (err) {
      toastError(err)
      setLessons(snapshot)
    } finally {
      setReorderingLessons(false)
    }
  }

  // ── Section handlers ──────────────────────────────────────────

  function onStartCreateSection() {
    setEditingSectionId(null)
    setSectionForm(initialSectionForm)
    setIsCreatingSection(true)
  }

  function onStartEditSection(section: CourseSection) {
    setIsCreatingSection(false)
    setEditingSectionId(section.id)
    setSectionForm({ title: section.title })
  }

  function onCancelSectionEdit() {
    setIsCreatingSection(false)
    setEditingSectionId(null)
    setSectionForm(initialSectionForm)
  }

  async function onSaveSection(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!id) return
    setSavingSection(true)
    try {
      if (editingSectionId) {
        await api.put(`/academy/courses/${id}/sections/${editingSectionId}`, sectionForm)
        toastSuccess("Sección actualizada")
      } else {
        await api.post(`/academy/courses/${id}/sections`, {
          title: sectionForm.title,
          order_index: sections.length,
        })
        toastSuccess("Sección creada")
      }
      onCancelSectionEdit()
      await loadSections()
    } catch (err) {
      toastError(err)
    } finally {
      setSavingSection(false)
    }
  }

  function onDeleteSection(sectionId: string) {
    if (!id) return
    setPendingConfirm({
      title: "¿Eliminar esta sección?",
      message: "Las lecciones quedarán sin sección asignada. Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      onConfirm: () => void confirmDeleteSection(sectionId),
    })
  }

  async function confirmDeleteSection(sectionId: string) {
    if (!id) return
    try {
      await api.delete(`/academy/courses/${id}/sections/${sectionId}`)
      toastSuccess("Sección eliminada")
      await loadSections()
      await loadLessons()
    } catch (err) {
      toastError(err)
    } finally {
      setPendingConfirm(null)
    }
  }

  function toggleSectionCollapse(sectionId: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) next.delete(sectionId)
      else next.add(sectionId)
      return next
    })
  }

  async function persistSectionOrder(nextSections: CourseSection[]) {
    if (!id) return
    setReorderingSections(true)
    try {
      await Promise.all(
        nextSections.map((section, index) => api.put(`/academy/courses/${id}/sections/${section.id}`, { order_index: index })),
      )
      setSections(nextSections.map((s, i) => ({ ...s, orderIndex: i })))
    } catch (err) {
      toastError(err)
    } finally {
      setReorderingSections(false)
    }
  }

  function moveSection(sectionId: string, direction: "up" | "down") {
    const sorted = [...sections].sort((a, b) => a.orderIndex - b.orderIndex)
    const index = sorted.findIndex((s) => s.id === sectionId)
    if (index === -1) return
    const targetIndex = direction === "up" ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= sorted.length) return
    const next = [...sorted]
    ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
    void persistSectionOrder(next)
  }

  // Arrastre de secciones: el hover reordena en vivo el estado local; al
  // soltar se persiste el orden completo (barato — pocas secciones por curso).
  function moveSectionHover(dragIndex: number, hoverIndex: number) {
    if (dragIndex === hoverIndex) return
    setSections((prev) => {
      const sorted = [...prev].sort((a, b) => a.orderIndex - b.orderIndex)
      const next = [...sorted]
      const [moved] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, moved)
      return next.map((s, i) => ({ ...s, orderIndex: i }))
    })
  }

  function onSectionDragEnd() {
    void persistSectionOrder([...sections].sort((a, b) => a.orderIndex - b.orderIndex))
  }

  // ── Instructor handlers ────────────────────────────────────────

  async function onAddInstructor() {
    if (!id || !pendingInstructor) return
    setSavingInstructor(true)
    try {
      await api.post(`/academy/courses/${id}/instructors`, {
        user_id: pendingInstructor.id,
        role: pendingInstructorRole,
        order_index: instructors.length,
      })
      toastSuccess("Instructor agregado")
      setPendingInstructor(null)
      await loadInstructors()
    } catch (err) {
      toastError(err)
    } finally {
      setSavingInstructor(false)
    }
  }

  function onRemoveInstructor(userId: string) {
    if (!id) return
    setPendingConfirm({
      title: "¿Quitar este instructor del curso?",
      message: "Podrás volver a agregarlo después si te equivocas.",
      confirmLabel: "Quitar",
      onConfirm: () => void confirmRemoveInstructor(userId),
    })
  }

  async function confirmRemoveInstructor(userId: string) {
    if (!id) return
    setRemovingInstructorId(userId)
    try {
      await api.delete(`/academy/courses/${id}/instructors/${userId}`)
      toastSuccess("Instructor removido")
      await loadInstructors()
    } catch (err) {
      toastError(err)
    } finally {
      setRemovingInstructorId(null)
      setPendingConfirm(null)
    }
  }

  // ── Prerequisite handlers ──────────────────────────────────────

  async function onAddPrerequisite() {
    if (!id || !pendingPrerequisite) return
    setSavingPrerequisite(true)
    try {
      await api.post(`/academy/courses/${id}/prerequisites`, {
        prerequisite_course_id: pendingPrerequisite.id,
      })
      toastSuccess("Curso previo agregado")
      setPendingPrerequisite(null)
      await loadPrerequisites()
    } catch (err) {
      toastError(err)
    } finally {
      setSavingPrerequisite(false)
    }
  }

  function onRemovePrerequisite(prerequisiteCourseId: string) {
    if (!id) return
    setPendingConfirm({
      title: "¿Quitar este curso previo requerido?",
      message: "Los estudiantes ya no necesitarán completar ese curso antes de este.",
      confirmLabel: "Quitar",
      onConfirm: () => void confirmRemovePrerequisite(prerequisiteCourseId),
    })
  }

  async function confirmRemovePrerequisite(prerequisiteCourseId: string) {
    if (!id) return
    setRemovingPrerequisiteId(prerequisiteCourseId)
    try {
      await api.delete(`/academy/courses/${id}/prerequisites/${prerequisiteCourseId}`)
      toastSuccess("Curso previo removido")
      await loadPrerequisites()
    } catch (err) {
      toastError(err)
    } finally {
      setRemovingPrerequisiteId(null)
      setPendingConfirm(null)
    }
  }

  // ── Programs handlers ──────────────────────────────────────────

  async function onAddProgram() {
    if (!id || !pendingProgram) return
    setSavingProgram(true)
    try {
      const detail = await api.get(`/academy/programs/admin/${pendingProgram.id}`)
      const existing: string[] = (detail.data?.data?.courses ?? []).map((cp: { courseId: string }) => cp.courseId)
      if (!existing.includes(id)) {
        await api.post(`/academy/programs/admin/${pendingProgram.id}/courses`, { course_ids: [...existing, id] })
      }
      toastSuccess("Curso agregado a la ruta académica")
      setAssignedProgramIds((prev) => [...prev, pendingProgram.id])
      setPendingProgram(null)
    } catch (err) {
      toastError(err)
    } finally {
      setSavingProgram(false)
    }
  }

  function onRemoveProgram(programId: string) {
    if (!id) return
    setPendingConfirm({
      title: "¿Quitar este curso de esta ruta académica?",
      message: "El curso dejará de contar para esa ruta.",
      confirmLabel: "Quitar",
      onConfirm: () => void confirmRemoveProgram(programId),
    })
  }

  async function confirmRemoveProgram(programId: string) {
    if (!id) return
    setRemovingProgramId(programId)
    try {
      await api.delete(`/academy/programs/admin/${programId}/courses/${id}`)
      toastSuccess("Curso removido de la ruta académica")
      setAssignedProgramIds((prev) => prev.filter((pId) => pId !== programId))
    } catch (err) {
      toastError(err)
    } finally {
      setRemovingProgramId(null)
      setPendingConfirm(null)
    }
  }

  // ── DataTable columns ──────────────────────────────────────────

  const instructorColumns: DataTableColumn<CourseInstructor>[] = [
    {
      key: "name",
      label: "Instructor",
      render: (inst) => (
        <div className="flex items-center gap-3">
          {inst.user.avatarUrl ? (
            <img src={inst.user.avatarUrl} alt={inst.user.name} className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 text-xs font-bold">
              {inst.user.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{inst.user.name}</p>
            <p className="text-xs text-gray-500">{inst.user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Rol",
      render: (inst) => (
        <span className={`text-[11px] rounded-full px-2 py-0.5 font-semibold ${
          inst.role === "lead"
            ? "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
            : "bg-gray-100 text-gray-600 dark:bg-gray-800"
        }`}>
          {inst.role === "lead" ? "Principal" : "Colaborador"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (inst) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: removingInstructorId === inst.userId ? "Quitando..." : "Quitar del curso",
                icon: <TrashBinIcon />,
                onClick: () => void onRemoveInstructor(inst.userId),
                variant: "danger",
                disabled: removingInstructorId === inst.userId,
                requires: "academy:courses:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  const prerequisiteColumns: DataTableColumn<PrerequisiteCourse>[] = [
    { key: "title", label: "Curso previo" },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (p) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: removingPrerequisiteId === p.id ? "Quitando..." : "Quitar",
                icon: <TrashBinIcon />,
                onClick: () => void onRemovePrerequisite(p.id),
                variant: "danger",
                disabled: removingPrerequisiteId === p.id,
                requires: "academy:courses:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  const programColumns: DataTableColumn<{ id: string; name: string; slug: string; isPublished: boolean }>[] = [
    {
      key: "name",
      label: "Ruta académica",
      render: (p) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{p.name}</p>
          <p className="text-xs text-gray-500">{p.slug}</p>
        </>
      ),
    },
    {
      key: "isPublished",
      label: "Estado",
      render: (p) => (
        <span className={`text-[11px] rounded-full px-2 py-0.5 font-semibold ${
          p.isPublished
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
            : "bg-gray-100 text-gray-600 dark:bg-gray-800"
        }`}>
          {p.isPublished ? "Publicada" : "Borrador"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (p) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: removingProgramId === p.id ? "Quitando..." : "Quitar",
                icon: <TrashBinIcon />,
                onClick: () => void onRemoveProgram(p.id),
                variant: "danger",
                disabled: removingProgramId === p.id,
                requires: "academy:courses:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  const assignedPrograms = assignedProgramIds
    .map((pId) => allPrograms.find((p) => p.id === pId))
    .filter((p): p is { id: string; name: string; slug: string; isPublished: boolean; course_count?: number } => Boolean(p))

  // ── Loading / not-found states ───────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadState label="Cargando curso..." />
      </div>
    )
  }

  if (isEdit && !course) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
        <p className="text-gray-500">Curso no encontrado.</p>
        <button type="button" onClick={() => navigate("/academy/courses")} className="text-sm text-brand-500 hover:underline">
          ← Volver a cursos
        </button>
      </div>
    )
  }

  const lessonCount = course?.lesson_count ?? 0
  const publishedWithoutLessons = Boolean(course?.isPublished) && lessonCount === 0
  const publishedWithoutInstructors = Boolean(course?.isPublished) && instructors.length === 0

  return (
    <>
    <CourseShell
      mode={isEdit ? "edit" : "create"}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Contenido",
        { label: "Cursos", to: "/academy/courses" },
        isEdit ? course?.title ?? "Curso" : "Nuevo curso",
      ]}
      title={isEdit ? course?.title ?? "Curso" : "Nuevo curso"}
      subtitle={isEdit ? "Gestiona la información, el contenido y el equipo de este curso" : "Completa la información básica para crear el curso"}
      metrics={
        isEdit
          ? [
              {
                label: "Lecciones",
                value: String(lessonCount),
                tone: publishedWithoutLessons ? "warning" : "neutral",
                hint: publishedWithoutLessons ? "Publicado sin contenido: los estudiantes no verán lecciones." : undefined,
              },
              { label: "Estudiantes", value: String(course?.student_count ?? 0) },
              {
                label: "Instructores",
                value: String(instructors.length),
                tone: publishedWithoutInstructors ? "warning" : "neutral",
                hint: publishedWithoutInstructors ? "Publicado sin nadie identificado como responsable." : undefined,
              },
            ]
          : []
      }
      headerActions={
        isEdit && course ? (
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${difficultyTone(course.difficultyLevel)}`}>
              {course.difficultyLevel}
            </span>
            <button
              type="button"
              onClick={() => void onTogglePublish()}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-opacity ${
                course.isPublished
                  ? "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
              }`}
              title={course.isPublished ? "Click para despublicar" : "Click para publicar"}
            >
              {course.isPublished ? "Publicado" : "Borrador"}
            </button>
          </div>
        ) : undefined
      }
    >
      {/* TAB: Información */}
      {activeTab === "info" && (
        <form onSubmit={onSaveInfo}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Identidad */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Identidad</h3>
              <input
                required
                autoFocus
                placeholder="Título del curso"
                value={infoForm.title}
                onChange={(e) => setInfoForm((p) => ({ ...p, title: e.target.value }))}
                className={inputCls}
              />
              <div>
                <label className={labelCls}>Categoría</label>
                <TagSearchInput
                  value={infoForm.category}
                  onChange={(category) => setInfoForm((p) => ({ ...p, category }))}
                  options={COURSE_CATEGORIES}
                  placeholder="Buscar o escribir una categoría…"
                />
              </div>
              <div>
                <label className={labelCls}>
                  Palabras clave <span className="font-normal text-gray-400">(opcional)</span>
                </label>
                <ChipInput
                  value={infoForm.tags}
                  onChange={(tags) => setInfoForm((p) => ({ ...p, tags }))}
                  placeholder="ej: arduino, openrocket, inventor, c++…"
                />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Escribe y presiona Enter o coma para agregar. Ayuda a que el curso se encuentre al buscar por ese término.
                </p>
              </div>
              <div>
                <label className={labelCls}>Nivel de dificultad</label>
                <StyledSelect
                  value={infoForm.difficulty_level}
                  onChange={(e) => setInfoForm((p) => ({ ...p, difficulty_level: e.target.value as Difficulty }))}
                >
                  <option value="beginner">Básico</option>
                  <option value="intermediate">Intermedio</option>
                  <option value="advanced">Avanzado</option>
                </StyledSelect>
              </div>
            </div>

            {/* Descripción */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Descripción</h3>
              <textarea
                rows={2}
                placeholder="Descripción corta (aparece en el catálogo)"
                value={infoForm.short_description}
                onChange={(e) => setInfoForm((p) => ({ ...p, short_description: e.target.value }))}
                className={textareaCls}
              />
              <textarea
                rows={8}
                placeholder="Descripción completa (opcional)"
                value={infoForm.description}
                onChange={(e) => setInfoForm((p) => ({ ...p, description: e.target.value }))}
                className={`${textareaCls} lg:min-h-[11rem]`}
              />
            </div>

            {/* Portada */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Portada del curso</h3>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex aspect-video w-full max-w-xs shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-white/[0.03]">
                  {infoForm.thumbnail_url ? (
                    <img
                      src={infoForm.thumbnail_url}
                      alt="Vista previa de la portada"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none"
                      }}
                    />
                  ) : (
                    <span className="px-4 text-center text-xs text-gray-500 dark:text-gray-500">Sin portada</span>
                  )}
                </div>
                <div className="flex-1">
                  <label className={labelCls}>URL de la imagen</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={infoForm.thumbnail_url}
                    onChange={(e) => setInfoForm((p) => ({ ...p, thumbnail_url: e.target.value }))}
                    className={inputCls}
                  />
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-500">
                    Se muestra en el catálogo, el dashboard del estudiante y las tarjetas de curso del semillero.
                  </p>
                </div>
              </div>
            </div>

            {/* Configuración */}
            <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Configuración</h3>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className={labelCls}>Horas estimadas</label>
                  <input
                    type="number"
                    min={0}
                    value={infoForm.estimated_hours}
                    onChange={(e) => setInfoForm((p) => ({ ...p, estimated_hours: Number(e.target.value) }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Precio (COP)</label>
                  <input
                    type="number"
                    min={0}
                    value={infoForm.price}
                    disabled={infoForm.is_free}
                    onChange={(e) => setInfoForm((p) => ({ ...p, price: Number(e.target.value) }))}
                    className={`${inputCls} disabled:opacity-40`}
                  />
                </div>
                <Toggle
                  checked={infoForm.is_free}
                  onChange={(v) => setInfoForm((p) => ({ ...p, is_free: v }))}
                  label="Gratuito"
                  description="Sin costo de inscripción"
                />
                <Toggle
                  checked={infoForm.is_cccd_course}
                  onChange={(v) => setInfoForm((p) => ({ ...p, is_cccd_course: v }))}
                  label="Curso para equipos CCCD"
                  description="Los exámenes IA de este curso se activan por equipo, no individuales"
                />
                {isEdit && (
                  <Toggle
                    checked={infoForm.is_published}
                    onChange={(v) => setInfoForm((p) => ({ ...p, is_published: v }))}
                    label="Publicado"
                    description="Visible en el catálogo"
                  />
                )}
              </div>
              {!isEdit && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  El curso se crea como borrador. Una vez creado podrás agregar contenido y publicarlo desde esta misma página.
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={() => navigate("/academy/courses")} className={btnSecondary}>
              Cancelar
            </button>
            <button type="submit" disabled={savingInfo} className={btnSave}>
              {savingInfo ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear curso"}
            </button>
          </div>
        </form>
      )}

      {/* TAB: Contenido (secciones + lecciones) */}
      {activeTab === "contenido" && (() => {
        const totalMinutes = lessons.reduce((sum, l) => sum + (l.durationMinutes ?? 0), 0)
        const unsorted = [...lessons].filter(l => !l.sectionId).sort((a, b) => a.orderIndex - b.orderIndex)
        const hasNoSections = sections.length === 0
        const sortedSections = [...sections].sort((a, b) => a.orderIndex - b.orderIndex)

        return (
          <DndProvider backend={HTML5Backend}>
            <div className="space-y-4">
              {/* Header card */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
                    Contenido del curso
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {lessons.length} lección{lessons.length !== 1 ? "es" : ""}
                    {totalMinutes > 0 ? ` · ${totalMinutes} min` : ""}
                    {sections.length > 0 ? ` · ${sections.length} sección${sections.length !== 1 ? "es" : ""}` : ""}
                  </p>
                </div>
                <button type="button" onClick={onStartCreateSection}
                  disabled={isCreatingSection || Boolean(editingSectionId)}
                  className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-40">
                  + Sección
                </button>
              </div>

              {/* New section card — sits above the sections/lessons list, only the name is required */}
              {isCreatingSection && (
                <form onSubmit={onSaveSection}
                  className="flex items-end gap-2 rounded-2xl border border-brand-200 bg-brand-50/40 p-5 dark:border-brand-500/30 dark:bg-brand-500/5">
                  <div className="flex-1">
                    <label className={labelCls}>Nombre de la sección</label>
                    <input required autoFocus placeholder="Ej: Introducción al curso"
                      value={sectionForm.title}
                      onChange={e => setSectionForm(f => ({ ...f, title: e.target.value }))}
                      className={inputCls} />
                  </div>
                  <button type="button" onClick={onCancelSectionEdit} className={btnSecondary}>Cancelar</button>
                  <button type="submit" disabled={savingSection} className={btnSave}>
                    {savingSection ? "Creando..." : "Crear sección"}
                  </button>
                </form>
              )}

              {/* Sections + lessons list */}
              <div className="rounded-[28px] border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
                {loadingSections ? (
                  <LoadState label="Cargando secciones..." />
                ) : hasNoSections && !isCreatingSection ? (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-10 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Este curso todavía no tiene secciones.</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Las lecciones solo se pueden crear dentro de una sección — crea la primera para empezar.</p>
                    <button type="button" onClick={onStartCreateSection}
                      className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
                      + Crear primera sección
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sortedSections.map((section, sIdx) => {
                      const sectionLessons = [...lessons]
                        .filter(l => l.sectionId === section.id)
                        .sort((a, b) => a.orderIndex - b.orderIndex)
                      return (
                        <SectionCard
                          key={section.id}
                          section={section}
                          index={sIdx}
                          totalSections={sortedSections.length}
                          sectionLessons={sectionLessons}
                          isCollapsed={collapsedSections.has(section.id)}
                          isEditing={editingSectionId === section.id}
                          sectionForm={sectionForm}
                          savingSection={savingSection}
                          reorderingLessons={reorderingLessons}
                          reorderingSections={reorderingSections}
                          onToggleCollapse={() => toggleSectionCollapse(section.id)}
                          onStartEdit={() => onStartEditSection(section)}
                          onCancelEdit={onCancelSectionEdit}
                          onSave={onSaveSection}
                          onDelete={() => void onDeleteSection(section.id)}
                          onSetTitle={(title) => setSectionForm({ title })}
                          onMoveUp={() => moveSection(section.id, "up")}
                          onMoveDown={() => moveSection(section.id, "down")}
                          onMoveSectionHover={moveSectionHover}
                          onSectionDragEnd={onSectionDragEnd}
                          onStartCreateLesson={() => onStartCreateLesson(section.id)}
                          onEditLesson={onStartEditLesson}
                          onDeleteLesson={(lessonId) => void onDeleteLesson(lessonId)}
                          onMoveLessonUp={(lessonId) => moveLesson(lessonId, "up")}
                          onMoveLessonDown={(lessonId) => moveLesson(lessonId, "down")}
                          onToggleLessonPreview={(lessonId) => void onToggleLessonPreview(lessonId)}
                          onLessonHoverMove={moveLessonHover}
                          onLessonDragStart={onLessonDragStart}
                          onLessonDragEnd={() => void onLessonDragEnd()}
                        />
                      )
                    })}

                    {/* Lecciones heredadas sin sección — solo lectura/arrastrables hacia una sección real */}
                    {unsorted.length > 0 && (
                      <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="flex items-center gap-2 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-3">
                          <p className="flex-1 font-semibold text-sm text-gray-500 dark:text-gray-400">Sin sección</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Arrastra estas lecciones a una sección, o usa el selector de cada una</p>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                          {unsorted.map((lesson, idx) => (
                            <div key={lesson.id} className="flex items-center gap-2 pr-4">
                              <div className="flex-1 min-w-0">
                                <LessonRow
                                  lesson={lesson}
                                  index={idx}
                                  containerId={null}
                                  allowDrop={false}
                                  isFirst={idx === 0}
                                  isLast={idx === unsorted.length - 1}
                                  reordering={reorderingLessons}
                                  onEdit={() => onStartEditLesson(lesson)}
                                  onDelete={() => void onDeleteLesson(lesson.id)}
                                  onMoveUp={() => moveLesson(lesson.id, "up")}
                                  onMoveDown={() => moveLesson(lesson.id, "down")}
                                  onTogglePreview={() => void onToggleLessonPreview(lesson.id)}
                                  onHoverMove={moveLessonHover}
                                  onDragStart={onLessonDragStart}
                                  onDragEnd={() => void onLessonDragEnd()}
                                />
                              </div>
                              {sortedSections.length > 0 && (
                                <div className="w-44 shrink-0">
                                  <StyledSelect
                                    value=""
                                    disabled={reorderingLessons}
                                    onChange={(e) => {
                                      if (e.target.value) void moveLessonToSection(lesson.id, e.target.value)
                                    }}
                                  >
                                    <option value="">Mover a sección...</option>
                                    {sortedSections.map((s) => (
                                      <option key={s.id} value={s.id}>{s.title}</option>
                                    ))}
                                  </StyledSelect>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DndProvider>
        )
      })()}

      {/* TAB: Instructores y prerrequisitos */}
      {activeTab === "equipo" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
              Instructores {instructors.length > 0 ? `(${instructors.length})` : ""}
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Busca y agrega instructores responsables de este curso.
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <div className="flex-1">
                <SearchMultiSelect
                  selectedOptions={pendingInstructor ? [{ id: pendingInstructor.id, label: pendingInstructor.name, sublabel: pendingInstructor.email }] : []}
                  onToggle={(option) => {
                    if (pendingInstructor?.id === option.id) {
                      setPendingInstructor(null)
                      return
                    }
                    const found = allUsers.find((u) => u.id === option.id)
                    if (found) setPendingInstructor(found)
                  }}
                  onSearch={async (query) => {
                    const q = query.trim().toLowerCase()
                    const assignedIds = new Set(instructors.map((i) => i.userId))
                    return allUsers
                      .filter((u) => !assignedIds.has(u.id))
                      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
                      .map((u) => ({ id: u.id, label: u.name, sublabel: u.email }))
                  }}
                  placeholder="Buscar usuario por nombre o correo..."
                  emptyMessage="No hay usuarios con ese criterio."
                />
              </div>
              <StyledSelect
                value={pendingInstructorRole}
                onChange={(e) => setPendingInstructorRole(e.target.value as "lead" | "contributor")}
              >
                <option value="lead">Principal</option>
                <option value="contributor">Colaborador</option>
              </StyledSelect>
            </div>

            {pendingInstructor && (
              <button type="button" disabled={savingInstructor}
                onClick={() => void onAddInstructor()}
                className="mt-3 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
                {savingInstructor ? "Agregando..." : `Agregar a ${pendingInstructor.name}`}
              </button>
            )}
          </div>

          <DataTable
            columns={instructorColumns}
            rows={instructors.slice((instructorsPage - 1) * INSTRUCTORS_PAGE_SIZE, instructorsPage * INSTRUCTORS_PAGE_SIZE)}
            rowKey={(inst) => inst.id}
            emptyMessage="Sin instructores asignados."
            page={instructorsPage}
            pageSize={INSTRUCTORS_PAGE_SIZE}
            total={instructors.length}
            onPageChange={setInstructorsPage}
          />

          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
              Cursos previos requeridos {prerequisites.length > 0 ? `(${prerequisites.length})` : ""}
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Se muestran como chips clicables en la página del curso — esta es la fuente de verdad, no el texto libre de "Requisitos".
            </p>

            <SearchMultiSelect
              selectedOptions={pendingPrerequisite ? [{ id: pendingPrerequisite.id, label: pendingPrerequisite.title }] : []}
              onToggle={(option) => {
                if (pendingPrerequisite?.id === option.id) {
                  setPendingPrerequisite(null)
                  return
                }
                const found = allCourses.find((c) => c.id === option.id)
                if (found) setPendingPrerequisite(found)
              }}
              onSearch={async (query) => {
                const q = query.trim().toLowerCase()
                const assignedIds = new Set(prerequisites.map((p) => p.id))
                return allCourses
                  .filter((c) => !assignedIds.has(c.id))
                  .filter((c) => c.title.toLowerCase().includes(q))
                  .map((c) => ({ id: c.id, label: c.title }))
              }}
              placeholder="Buscar curso por nombre..."
              emptyMessage="No hay cursos con ese criterio."
            />

            {pendingPrerequisite && (
              <button type="button" disabled={savingPrerequisite}
                onClick={() => void onAddPrerequisite()}
                className="mt-3 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
                {savingPrerequisite ? "Agregando..." : `Agregar "${pendingPrerequisite.title}"`}
              </button>
            )}
          </div>

          <DataTable
            columns={prerequisiteColumns}
            rows={prerequisites.slice((prerequisitesPage - 1) * PREREQUISITES_PAGE_SIZE, prerequisitesPage * PREREQUISITES_PAGE_SIZE)}
            rowKey={(p) => p.id}
            emptyMessage="Sin cursos previos configurados."
            page={prerequisitesPage}
            pageSize={PREREQUISITES_PAGE_SIZE}
            total={prerequisites.length}
            onPageChange={setPrerequisitesPage}
          />
        </div>
      )}

      {/* TAB: Rutas académicas */}
      {activeTab === "programas" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
              Rutas académicas {assignedPrograms.length > 0 ? `(${assignedPrograms.length})` : ""}
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Busca y agrega las rutas académicas en las que debe aparecer este curso.
            </p>

            {allPrograms.length === 0 && !loadingPrograms ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-10 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">No hay rutas académicas creadas.</p>
                <a href="/programs/programs" className="mt-2 inline-block text-sm text-brand-500 hover:underline">
                  Ir a Rutas académicas →
                </a>
              </div>
            ) : (
              <>
                <SearchMultiSelect
                  selectedOptions={pendingProgram ? [{ id: pendingProgram.id, label: pendingProgram.name }] : []}
                  onToggle={(option) => {
                    if (pendingProgram?.id === option.id) {
                      setPendingProgram(null)
                      return
                    }
                    const found = allPrograms.find((p) => p.id === option.id)
                    if (found) setPendingProgram({ id: found.id, name: found.name })
                  }}
                  onSearch={async (query) => {
                    const q = query.trim().toLowerCase()
                    return allPrograms
                      .filter((p) => !assignedProgramIds.includes(p.id))
                      .filter((p) => p.name.toLowerCase().includes(q))
                      .map((p) => ({ id: p.id, label: p.name, sublabel: p.isPublished ? undefined : "Borrador" }))
                  }}
                  placeholder="Buscar ruta académica por nombre..."
                  emptyMessage="No hay rutas académicas con ese criterio."
                />

                {pendingProgram && (
                  <button type="button" disabled={savingProgram}
                    onClick={() => void onAddProgram()}
                    className="mt-3 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
                    {savingProgram ? "Agregando..." : `Agregar a "${pendingProgram.name}"`}
                  </button>
                )}
              </>
            )}
          </div>

          <DataTable
            columns={programColumns}
            rows={assignedPrograms.slice((programsPage - 1) * PROGRAMS_PAGE_SIZE, programsPage * PROGRAMS_PAGE_SIZE)}
            rowKey={(p) => p.id}
            loading={loadingPrograms}
            emptyMessage="Sin rutas académicas asignadas."
            page={programsPage}
            pageSize={PROGRAMS_PAGE_SIZE}
            total={assignedPrograms.length}
            onPageChange={setProgramsPage}
          />
        </div>
      )}
    </CourseShell>
    <ConfirmDialog
      isOpen={pendingConfirm !== null}
      title={pendingConfirm?.title ?? ""}
      message={pendingConfirm?.message ?? ""}
      confirmLabel={pendingConfirm?.confirmLabel ?? "Eliminar"}
      onConfirm={() => pendingConfirm?.onConfirm()}
      onCancel={() => setPendingConfirm(null)}
    />
    </>
  )
}
